/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import NavbarLangOnly from "../../components/Home/navbarLang/NavbarLangOnly";
import PageContainer from "../../components/Home/layout/PageContainer";
import "react-toastify/dist/ReactToastify.css";
import {
  getSubscriptionPaymentContact,
  initiateSubscriptionPayment,
} from "../../Stores/utils/storeApi";

export default function PaymentPage() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);

  const isUpgrade =
    new URLSearchParams(window.location.search).get("upgrade") === "true";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const COLORS = {
    darkPurple: "#21003D",
    purple: "#7400BF",
    gold: "#F2B441",
    textLight: "#f3e9ff",
  };

  useEffect(() => {
    const startSubscriptionPayment = async () => {
      const tenantId = parseInt(localStorage.getItem("tenantId") || "0", 10);
      const planId = parseInt(
        (isUpgrade
          ? localStorage.getItem("upgradePlanId")
          : localStorage.getItem("selectedPlanId")) || "0",
        10
      );

      if (!tenantId || !planId) {
        toast.error(
          isArabic
            ? "بيانات الخطة أو المتجر غير مكتملة"
            : "Plan or store data is incomplete",
          { rtl: isArabic, theme: "colored" }
        );
        setLoading(false);
        setTimeout(() => navigate(isUpgrade ? "/admin-store/subscriptions" : "/theme-plan"), 2000);
        return;
      }

      const contact = getSubscriptionPaymentContact();

      try {
        const paymentRes = await initiateSubscriptionPayment({
          tenantId,
          planId,
          email: contact.email,
          phone: contact.phone,
          firstName: contact.firstName,
          lastName: contact.lastName,
        });

        if (!paymentRes?.success || !paymentRes?.paymentUrl) {
          toast.error(
            paymentRes?.error ||
              (isArabic ? "فشل فتح صفحة الدفع" : "Failed to open payment page"),
            { rtl: isArabic, theme: "colored" }
          );
          setLoading(false);
          return;
        }

        localStorage.setItem("pendingPaymentUpgrade", isUpgrade ? "true" : "false");
        window.location.href = String(paymentRes.paymentUrl).trim();
      } catch (err) {
        console.error("Subscription payment error:", err);
        toast.error(
          isArabic ? "حدث خطأ أثناء بدء الدفع" : "An error occurred while starting payment",
          { rtl: isArabic, theme: "colored" }
        );
        setLoading(false);
      }
    };

    startSubscriptionPayment();
  }, [isArabic, isUpgrade, navigate]);

  return (
    <>
      <NavbarLangOnly />
      <ToastContainer />

      <PageContainer title={isArabic ? "الدفع" : "Payment"}>
        <div
          className="text-center"
          style={{
            color: COLORS.textLight,
            fontFamily: "'Tajawal', 'Cairo', sans-serif",
            marginTop: "20px",
            padding: isMobile ? "40px 16px" : "60px 24px",
          }}
        >
          <h5
            style={{
              color: COLORS.gold,
              fontWeight: "700",
              marginBottom: "24px",
              fontSize: isMobile ? "1.1rem" : "1.3rem",
            }}
          >
            {isUpgrade
              ? isArabic
                ? "جارٍ تحويلك لإتمام ترقية الخطة..."
                : "Redirecting to complete your plan upgrade..."
              : isArabic
                ? "جارٍ تحويلك لإتمام الدفع..."
                : "Redirecting to complete your payment..."}
          </h5>

          {loading && (
            <div
              className="spinner-border"
              role="status"
              style={{ color: COLORS.gold, width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
        </div>
      </PageContainer>
    </>
  );
}
